/**
 * LearnTipsOverlay
 *
 * Rendered when the Learn Tips toggle (`useLearnTipsStore`) is ON. A thin
 * skin over the shared `@plerity/learn-mode` `LearnModeOverlay` (migrated
 * 2026-10-16 off social's own fork): it scans the live DOM for
 * `[data-help-context]` elements and paints a small "?" badge near each one
 * whose key resolves to a registered `LearnTip` (`config/learn-tips`).
 * Hovering or clicking the badge opens a popover with the tip's title, body
 * and optional bullets; Escape, an outside click, a second click, or leaving
 * the popover closes it.
 *
 * Placement is the package's opt-in obstacle-aware mode — the same algorithm
 * social shipped 2026-10-04 (live QA: header / inbox / evergreen / editor-ai
 * badges painting over controls), upstreamed into the package:
 *
 *   - `placement="floating"` + `avoidObstacles` — every badge lives in one
 *     body-level `position: fixed` layer, and each candidate spot (6 around the
 *     target, then an outward search, then a reduced-size inside-corner spot)
 *     is point-sampled with `document.elementsFromPoint` against controls,
 *     media and text. If nothing is free the anchor gets NO badge — a missing
 *     badge beats one sitting on a control. Re-measured on scroll (capture
 *     phase), resize, target resize and debounced DOM mutations.
 *   - `obstacleOptions` is omitted on purpose: the package defaults
 *     (`DEFAULT_INTERACTIVE_SELECTOR` / `DEFAULT_MEDIA_SELECTOR` /
 *     `DEFAULT_IGNORE_SELECTOR`) ARE social's original selector set.
 *   - `excludeContextKeys` drops the Konva canvas wrapper
 *     (`design-editor-canvas`, `components/design-editor/Canvas.tsx`) — a
 *     badge near its corner would sit on canvas pointer interaction. The tip
 *     stays in the registry for coverage; it is just never badge-rendered.
 *   - `zIndex.layer="tooltip"` = 1050, the `@plerity/tokens-apps` preset's
 *     `zIndex.tooltip` — above the `z-50` chat FAB and `z-60` modal overlays.
 *
 * Keyboard: the badge is a real `<button>` (Enter/Space toggles the popover,
 * Escape closes it). The package does not open on mere focus (social's fork
 * did); click-to-open is the WAI-ARIA disclosure pattern and avoids popovers
 * flashing open while tabbing through a dense toolbar.
 *
 * Every Tailwind class string lives HERE (the package is behaviour only), so
 * social's own Tailwind scan generates them.
 */

import { type LearnModeClassNames, LearnModeOverlay } from "@plerity/learn-mode";
import { getTipByContextKey } from "../../config/learn-tips";

/** Context keys that are never badge-rendered even though they have a tip. */
const EXCLUDED_CONTEXT_KEYS: readonly string[] = ["design-editor-canvas"];

/** Social has no per-page remote tips or tours — one static page key. */
const PAGE_KEY = "social";

const CLASS_NAMES: LearnModeClassNames = {
  // 20px (`h-5 w-5`) badge — matches the package's floating BADGE_SIZE used
  // for placement math. Position / z-index / pointer-events are inline.
  badge: [
    "flex h-5 w-5 items-center justify-center rounded-full text-3xs font-bold shadow-sm",
    "transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-1",
  ].join(" "),
  badgeIdle:
    "bg-gray-300/80 text-gray-700 opacity-60 hover:opacity-100 hover:bg-brand-500 hover:text-white dark:bg-gray-600/80 dark:text-gray-200 dark:hover:bg-brand-500",
  badgeActive: "bg-brand text-brand-fg dark:bg-brand-500",
  // Last-resort inside-corner spot (package sets an inline 16px size). Quieter
  // idle look; `!` so it beats `badgeIdle`'s `opacity-60` regardless of
  // stylesheet order, with hover / open restoring full opacity.
  badgeReduced: "opacity-40! hover:opacity-100! aria-expanded:opacity-100!",
  popover: "rounded-lg border border-gray-200 bg-white p-4 text-left shadow-xl dark:border-gray-700 dark:bg-gray-800",
  title: "mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100",
  body: "text-sm leading-relaxed text-gray-600 dark:text-gray-400",
  bulletList: "mb-0 mt-2 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-600 dark:text-gray-400",
};

export default function LearnTipsOverlay() {
  return (
    <LearnModeOverlay
      pageKey={PAGE_KEY}
      placement="floating"
      avoidObstacles
      excludeContextKeys={EXCLUDED_CONTEXT_KEYS}
      zIndex={{ layer: "tooltip" }}
      resolveStaticTip={getTipByContextKey}
      openOnHover
      showCloseButton={false}
      classNames={CLASS_NAMES}
    />
  );
}
